import { AsyncPipe } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { AppLifecycleService } from "@app/app-lifecycle.service";
import { AuthError, AuthErrorCode, AuthService } from "@app/auth.service";
import { MasterDataInitStatus, MasterDataService } from "@app/master-data.service";
import { FullScreenErrorComponent } from "@components/full-screen-error/full-screen-error.component";
import { LogoComponent } from "@components/logo/logo.component";
import { ProjectService } from "@domain/project/project.service";
import { LoadingSpinnerDirective } from "@odx/angular/components/loading-spinner";
import { ToastContainerComponent } from "@odx/angular/components/toast";
import { catchError, filter, map, Observable, of, switchMap } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [LoadingSpinnerDirective, LogoComponent, AsyncPipe, ToastContainerComponent, FullScreenErrorComponent, RouterOutlet],
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private masterDataService = inject(MasterDataService);
  private projectService = inject(ProjectService);
  private readonly router = inject(Router);

  protected readonly AuthError = AuthErrorCode;
  protected readonly MasterDataInitStatus = MasterDataInitStatus;

  protected readonly errorMessageLoadingProductData: string[] = [
    $localize`:@@app.errorMessage.loadingProductData.text:Versuchen Sie es später nochmal. Sollte das Problem weiterhin bestehen, kontaktieren Sie bitte Ihren Administrator.`,
  ];
  protected readonly navintaTeamEmailLink: string =
    "<b><a href=mailto:navinta@draeger.com?subject=Zugang%20zu%20Navinta>navinta@draeger.com</a></b>";
  protected readonly errorMessageNoAccess: string[] = [
    $localize`:@@app.errorMessage.noAccess.text1:Aktuell befinden wir uns noch in der Testphase und der Zugang zur Anwendung ist eingeschränkt.`,
    $localize`:@@app.errorMessage.noAccess.text2:Sollten Sie Fragen haben oder den Wunsch nach Zugang zu Navinta, zögern Sie bitte nicht, sich unter ${this.navintaTeamEmailLink}:navintaTeamEmailLink: an das Navinta-Team zu wenden. Wir bemühen uns, Ihre Anliegen so schnell wie möglich zu bearbeiten.`,
    "",
    $localize`:@@app.errorMessage.noAccess.text3:Danke für Ihr Verständnis und Ihre Unterstützung.`,
    "",
    $localize`:@@app.errorMessage.noAccess.text4:Beste Grüße,`,
    $localize`:@@app.errorMessage.noAccess.text5:Ihr Navinta-Team`,
  ];
  protected readonly errorMessageInvalidLogin: string[] = [
    $localize`:@@app.errorMessage.invalidLogin.text:Es gibt Probleme mit Ihrem Benutzer-Login. Bitte kontaktieren Sie Ihren Administrator.`,
  ];
  protected readonly errorMessageOffline: string[] = [
    $localize`:@@app.errorMessage.offline.text:Derzeit sind Sie Offline, so dass ein Login nicht möglich ist. Bitte verbinden Sie Ihr Gerät mit dem Internet, um fortzufahren.`,
  ];

  isIframe = false;
  authError$?: Observable<AuthError | null>;
  initStatus$?: Observable<MasterDataInitStatus>;

  constructor() {
    // Instantiates the root-provided service purely for the side effect of its constructor, so it starts
    // listening for visibilitychange/pagehide/online as soon as the app boots. Not stored in a field - there
    // is no public API to call on it afterwards.
    inject(AppLifecycleService);
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;

    const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isReload = navEntries.length && navEntries[0].type === "reload";

    if (isReload) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1), // only after first navigation completes (after reload)
        )
        .subscribe(() => this.handleWritePermissionCheckOnReload());
    }

    // wait to prevent from showing the loader when data initialization is fast
    setTimeout(() => {
      this.initStatus$ = this.masterDataService.initStatus$;
      this.authError$ = this.authService.userInfo$.pipe(
        map(() => null),
        catchError((error) => of(error)),
      );
    }, 500);
  }

  private handleWritePermissionCheckOnReload() {
    const path = this.router.url;
    if (this.hasUuid(path)) {
      const projectId = path.split("/")[2];
      this.projectService
        .getLocalProjectById(projectId)
        .pipe(
          filter((project) => !!project),
          take(1),
          switchMap((project) => {
            return this.projectService.updateUserWritePermission(project);
          }),
        )
        .subscribe();
    }
  }

  private hasUuid(input: string) {
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
    return uuidRegex.test(input);
  }
}
